import { useState, useEffect, useCallback } from 'preact/hooks';
import {
  fetchIntentDistribution,
  fetchIntentTransitions,
  type IntentDistributionEntry,
  type IntentTransitionEntry,
} from '../api/admin';
import { intentFriendlyLabel } from '../utils/intentLabel';

/**
 * model-meta-hardening Phase 7 — Intent Analytics admin tab.
 *
 * Read-only view over per-turn `chat_message.intentType` (Phase 1). Two panels:
 *  - distribution: a simple horizontal bar list (count + pct per intentType);
 *  - transitions:  a list of from→to edges with counts.
 * Days window selectable (1 / 7 / 14 / 30). Styled with theme CSS vars like
 * TierQuotasTab — no DOM-specific colors.
 */

const DAY_OPTIONS = [1, 7, 14, 30];

const sectionTitleStyle: Record<string, string | number> = {
  margin: '20px 0 10px',
  color: 'var(--text-primary, #c8c8e8)',
  fontSize: 14,
  fontWeight: 600,
};

const labelStyle = (intentType: string): string =>
  // Friendly label, but keep the raw token visible for operators who want it.
  `${intentFriendlyLabel(intentType) ?? intentType}`;

export function IntentAnalyticsTab() {
  const [days, setDays] = useState(7);
  const [distribution, setDistribution] = useState<IntentDistributionEntry[]>([]);
  const [transitions, setTransitions] = useState<IntentTransitionEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback((d: number) => {
    setLoading(true);
    setError('');
    Promise.all([fetchIntentDistribution(d), fetchIntentTransitions(d)])
      .then(([dist, trans]) => {
        setDistribution(dist.distribution);
        setTotal(dist.total);
        setTransitions(trans.transitions);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const maxCount = distribution.reduce((m, e) => Math.max(m, e.count), 0) || 1;
  const maxTransitionCount = transitions.reduce((m, e) => Math.max(m, e.count), 0) || 1;

  return (
    <div class="admin-content" style={{ padding: '12px 16px' }}>
      {/* Window selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ color: 'var(--text-secondary, #8888aa)', fontSize: 12 }}>Window</span>
        <select
          value={days}
          style={{ fontSize: 12, padding: '4px 8px', background: 'var(--bg-elevated, #1a1a2e)', color: 'var(--text-primary, #c8c8e8)', border: '1px solid var(--border, #4a4a6e)', borderRadius: 6, cursor: 'pointer' }}
          onChange={(e) => setDays(parseInt((e.target as HTMLSelectElement).value, 10))}
        >
          {DAY_OPTIONS.map((d) => (
            <option key={d} value={d}>last {d} {d === 1 ? 'day' : 'days'}</option>
          ))}
        </select>
        {loading && <span style={{ color: 'var(--text-secondary, #8888aa)', fontSize: 12 }}>loading…</span>}
      </div>

      {error && (
        <div class="message error" style={{ margin: '8px 0' }}>{error}</div>
      )}

      {/* Distribution */}
      <h3 style={sectionTitleStyle}>
        Intent distribution
        <span style={{ color: 'var(--text-secondary, #8888aa)', fontSize: 12, fontWeight: 400 }}>
          {' '}· {total} classified turns
        </span>
      </h3>
      {distribution.length === 0 ? (
        <div style={{ color: 'var(--text-secondary, #5a5a7e)', fontSize: 13, padding: '8px 0' }}>
          No classified turns in this window.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {distribution.map((e) => (
            <div key={e.intentType}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
                <span style={{ color: 'var(--text-primary, #c8c8e8)' }}>
                  {labelStyle(e.intentType)}
                  <span style={{ color: 'var(--text-secondary, #5a5a7e)', fontSize: 11 }}> ({e.intentType})</span>
                </span>
                <span style={{ color: 'var(--text-secondary, #8888aa)' }}>{e.count} · {e.pct}%</span>
              </div>
              <div style={{ background: 'var(--border, #2a2a3e)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${(e.count / maxCount) * 100}%`,
                    height: '100%',
                    background: 'var(--theme-accent, #6666cc)',
                    borderRadius: 4,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Transitions */}
      <h3 style={sectionTitleStyle}>Intent transitions</h3>
      <p style={{ color: 'var(--text-secondary, #5a5a7e)', fontSize: 11, margin: '0 0 8px' }}>
        How often one classified intent is followed by another within the same session
        (NULL turns skipped, no cross-session edges).
      </p>
      {transitions.length === 0 ? (
        <div style={{ color: 'var(--text-secondary, #5a5a7e)', fontSize: 13, padding: '8px 0' }}>
          No transitions in this window.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {transitions.map((t) => (
            <div
              key={`${t.from}->${t.to}`}
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
            >
              <span style={{ flex: 1, minWidth: 0, color: 'var(--text-primary, #c8c8e8)' }}>
                {labelStyle(t.from)} <span style={{ color: 'var(--text-secondary, #8888aa)' }}>→</span> {labelStyle(t.to)}
              </span>
              <div style={{ flex: 1, background: 'var(--border, #2a2a3e)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${(t.count / maxTransitionCount) * 100}%`,
                    height: '100%',
                    background: 'var(--theme-accent, #6666cc)',
                    borderRadius: 4,
                  }}
                />
              </div>
              <span style={{ color: 'var(--text-secondary, #8888aa)', minWidth: 28, textAlign: 'right' }}>{t.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
